import { observable, computed, makeObservable } from 'mobx'

import { required, func } from 'validx'
import { DocumentDTO } from './dtos/document'
import { GetCurrentMember } from 'iam/SessionStore'
import { Model } from 'infra/model'
import { maybeParseDate, DateZero } from 'utils/date-util'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import { Id, Nullable } from 'types/definitions'
import once from 'lodash/once'
import Workspace from 'iam/models/Workspace'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import DocumentType from './DocumentType'
import { PrimeDocumentType } from './DocumentTypeStore'
import { GetJob } from 'jobs/JobStore'
import Job from 'jobs/Job'
import compact from 'lodash/compact'
import { DocumentStage } from './DocumentStage'
import { DocumentRetention } from './DocumentRetention'
import { DocumentLinks } from './DocumentLinks'
import {
  mapAccessesToDocumentLinks,
  mapDocumentRetention,
  mapFileDescriptorToDocumentStage,
} from './document-mappers'

export default class Document extends Model<DocumentDTO, Document> {
  static validationRules = {
    title: [
      required('Title is required'),
      func((opts) => opts.value.length >= 2, 'Must be more than 2 characters.'),
    ],
  }

  @observable id = Id.none
  @observable workspaceId = Id.none
  @observable title = ''
  @observable description = ''
  @observable archived = false
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()

  @observable uploadProgress: Nullable<UploadProgress> = null

  @observable readonly owner = Member.none
  @observable readonly author = Member.none
  @observable readonly workspace = Workspace.none

  @observable documentType: Nullable<DocumentType> = null

  @observable stage: DocumentStage = DocumentStage.initial
  @observable links: DocumentLinks = DocumentLinks.empty
  @observable retention: DocumentRetention = DocumentRetention.active

  // TODO: will be removed when v5 is canonical.
  /** @deprecated */
  @observable canEdit = false
  /** @deprecated */
  @observable ephemeral = false

  static get none() {
    return lazyNone()
  }

  constructor(
    private primeMemberByPublicId: PrimeMemberByPublicId,
    private getCurrentMember: GetCurrentMember,
    private primeDocumentType: PrimeDocumentType,
    private primeWorkspace: PrimeWorkspace,
    private getJob: GetJob,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get type() {
    return this.documentType
  }

  @computed
  get downloadable() {
    return this.stage.type === 'Released' && this.retention.type === 'Active'
  }

  /**
   * Whether the document has been uploaded entirely. This does not
   * necessarily imply that the document is downloadable yet, though.
   */
  @computed
  get isFullyUploaded() {
    return this.stage.type === 'Released'
  }

  /**
   * Whether the document is currently being processed.
   */
  @computed
  get isProcessing() {
    return (
      this.stage.type === 'Preprocessing' ||
      this.stage.type === 'AttestationScreening'
    )
  }

  /**
   * Whether the document requires a person to verify it before release.
   */
  @computed
  get isManualAttestationRequired() {
    return this.stage.type === 'ManualAttestationRequired'
  }

  /**
   * Whether the document's contents have been purged.
   */
  @computed
  get isPurged() {
    return this.retention.type === 'Purged'
  }

  get purge() {
    return this.retention.type === 'Purged' && this.retention.purge
      ? {
          purgedAt: this.retention.purge.purgedAt,
          purgedBy: this.retention.purge.purgedBy,
        }
      : null
  }

  /**
   * The IDs of the jobs this document is linked to.
   */
  @computed
  get jobIds() {
    return this.links.job.jobIds
  }

  /**
   * The jobs this document is linked to.
   */
  @computed
  get linkedJobs(): Job[] {
    return compact(this.jobIds.map((id) => this.getJob(id)))
  }

  /**
   * Whether the document is linked to the specified job.
   */
  isLinkedToJob(jobId: Id): boolean {
    if (!jobId) {
      return false
    }

    return this.jobIds.includes(jobId)
  }

  /**
   * The file extension, derived from the title.
   */
  @computed
  get fileType() {
    return this.title.split('.').pop() || 'file'
  }

  @computed
  get canManageLinks() {
    const currentUser = this.getCurrentMember()
    return this.owner.user.id === currentUser.user.id
  }

  parse(json: Partial<DocumentDTO>): Partial<Document> {
    return {
      id: json.long_id,
      title: json.title,
      canEdit: json.can_edit,
      description: json.description,
      workspaceId: json.workspace_id,
      archived: json.archived,
      ephemeral: json.ephemeral,
      stage: json.latest_version
        ? mapFileDescriptorToDocumentStage(json.latest_version.files[0])
        : undefined,
      retention:
        json.archived !== undefined ? mapDocumentRetention(json) : undefined,
      links:
        json.accesses || json.access
          ? mapAccessesToDocumentLinks(json.accesses ?? [json.access!])
          : undefined,
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
      owner: this.primeMemberByPublicId({
        user_public_id: json.owner_public_id,
        workspace_id: json.workspace_id || this.workspaceId,
      }),
      author: this.primeMemberByPublicId({
        user_public_id: json.latest_version?.author_public_id,
        workspace_id: json.workspace_id || this.workspaceId,
      }),
      documentType:
        json.document_type && this.primeDocumentType(json.document_type),
      workspace: this.primeWorkspace({
        id: json.workspace_id,
      }),
    }
  }
}

export interface UploadProgress {
  uploaded: number
  percent: number
  total: number
}

const lazyNone = once(
  () =>
    new Document(
      () => Member.none,
      () => Member.none,
      () => undefined,
      () => undefined,
      () => undefined,
    ),
)
